import React, { useEffect, useState, useCallback } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Theme from './Theme';
import PageLayout from './PageLayout';
import WalletsSidebar from './WalletsSidebar';
import SourceAccounts from './SourceAccounts';


const HomePage = (props) => {
  const { currentUser, companies, currentCompany, sources, selectedSource, csrfToken } = props;

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState(null);
  const [apiAccounts, setApiAccounts] = useState([]);


  const fetchSourceAccounts = useCallback(async () => {
    if (!currentCompany?.id || !selectedSource?.id) { 
      setApiAccounts([]); 
      return; 
    }
    
    setIsLoadingAccounts(true);
    setAccountsError(null);
    
    const url = `/api/internal/companies/${currentCompany.id}/sources/${selectedSource.id}/accounts`;
    
    try {
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      
      const data = await res.json();
      // Safety filter: ensure external_id present
      const filtered = (data || []).filter(a => a.external_id && String(a.external_id).length > 0);
      setApiAccounts(filtered);
    } catch (e) {
      console.error('Failed to load source accounts', e);
      setAccountsError(e.message);
      setApiAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [currentCompany?.id, selectedSource?.id, csrfToken]);

  useEffect(() => { 
    fetchSourceAccounts(); 
  }, [fetchSourceAccounts]);

  const handleAccountClick = (account) => {
    // Navigate to account details page
    window.location.href = `/accounts/${account.id}`;
  };

  return (
    <Theme>
      <React.Fragment>
        {/* Sidebar */}
        <WalletsSidebar sources={sources} />
        
        {/* Main Content */}
        <PageLayout withSidebar={true} containerProps={{ sx: { backgroundColor: 'transparent', pb: 4, pt: 4 } }}>
          


          {/* Source Accounts Display */}
          {selectedSource ? (
            <SourceAccounts 
              selectedSource={selectedSource} 
              sourceAccounts={apiAccounts}
              isLoading={isLoadingAccounts}
              error={accountsError}
              onAccountClick={handleAccountClick}
              currentCompany={currentCompany}
              csrfToken={csrfToken}
            />
          ) : (
            <>
              {/* Dashboard Content Placeholder */}
              <Box sx={{ mb: 4, p: 3, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Quick Stats
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Dashboard content will be implemented here. This could include:
                </Typography>
                <Box component="ul" sx={{ mt: 2, color: 'text.secondary' }}>
                  <li>Recent transactions</li>
                  <li>Portfolio overview</li>
                  <li>P&L summaries</li>
                  <li>Tax reporting status</li>
                </Box>
              </Box>

              {/* Sources Debug Info */}
              <Box sx={{ mb: 4, p: 3, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Sources Summary
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Total configured sources: {sources.length}
                </Typography>
              </Box>
            </>
          )}
        </PageLayout>
      </React.Fragment>
    </Theme>
  );
};

// Keep PropTypes in HomePage as it receives the initial data from Rails
HomePage.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  companies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  currentCompany: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  sources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    display_name: PropTypes.string.isRequired,
    source_name: PropTypes.string.isRequired
  })).isRequired,
  selectedSource: PropTypes.shape({
    id: PropTypes.number.isRequired,
    display_name: PropTypes.string.isRequired,
    source_name: PropTypes.string.isRequired
  }),
  sourceAccounts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
    balance: PropTypes.number
  })).isRequired,
  csrfToken: PropTypes.string.isRequired
};

export default HomePage;
